import type { ReactNode } from 'react'
import { cn } from '@/internationalization/cn'

export interface TabItem {
  id: string
  label: string
  icon?: ReactNode
  /** Rendered as a subdued pill after the label — counts, not warnings. */
  count?: number
}

export interface TabsProps {
  tabs: TabItem[]
  activeId: string
  onChange: (id: string) => void
  /** 'underline' for page-level sections; 'pill' for a filter inside a card. */
  variant?: 'underline' | 'pill'
  className?: string
}

/**
 * Horizontal section switcher.
 *
 * The strip scrolls horizontally on small screens instead of wrapping — a
 * wrapped tab row changes the page's header height between tabs, which moves
 * the content the user is reading.
 */
export function Tabs({ tabs, activeId, onChange, variant = 'underline', className }: TabsProps) {
  return (
    <div
      role="tablist"
      className={cn(
        'no-scrollbar flex items-center gap-1 overflow-x-auto',
        variant === 'underline' && 'border-b border-border',
        variant === 'pill' && 'rounded-md bg-surface-muted p-1',
        className,
      )}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeId
        return (
          <button
            key={tab.id}
            role="tab"
            type="button"
            aria-selected={isActive}
            onClick={() => onChange(tab.id)}
            className={cn(
              'inline-flex shrink-0 items-center gap-2 whitespace-nowrap text-base font-medium transition-colors duration-fast',
              variant === 'underline' &&
                cn(
                  '-mb-px border-b-2 px-3 py-2.5',
                  isActive
                    ? 'border-brand text-fg'
                    : 'border-transparent text-fg-muted hover:border-border-strong hover:text-fg',
                ),
              variant === 'pill' &&
                cn(
                  'rounded px-3 py-1.5',
                  isActive ? 'bg-surface text-fg shadow-xs' : 'text-fg-muted hover:text-fg',
                ),
            )}
          >
            {tab.icon && <span aria-hidden className="[&>svg]:h-4 [&>svg]:w-4">{tab.icon}</span>}
            {tab.label}
            {tab.count !== undefined && (
              <span
                className={cn(
                  'rounded-full px-1.5 py-0.5 text-2xs font-semibold',
                  isActive ? 'bg-brand-subtle text-brand' : 'bg-surface-muted text-fg-subtle',
                )}
              >
                {tab.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
